import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { resolveImageUrl } from "@/lib/api";
import { MapPin, BedDouble, Ruler, Sparkles, BadgeCheck, ArrowRight } from "lucide-react";
import { RentalBadge } from "@/components/AssuredRental";

export default function PropertyCard({ property, onEnquire, featured = false }) {
  const { lang, t } = useLang();
  const title = (lang === "hi" && property.title_hi) || property.title_en;
  const cover = resolveImageUrl(property.image_paths?.[0]);
  const isRichFeatured = featured || property.is_exclusive || property.is_assured_return;

  return (
    <article
      data-testid={`property-card-${property.id}`}
      className={`group relative bg-white rounded-xl overflow-hidden border-2 transition-all duration-300 flex flex-col ${
        isRichFeatured
          ? "rc-card-featured border-[#0B2545] shadow-[0_14px_40px_-20px_rgba(11,37,69,0.6)] hover:shadow-[0_22px_55px_-18px_rgba(201,168,76,0.65)]"
          : "border-[#0B2545]/15 hover:border-[#0B2545] shadow-sm hover:shadow-xl"
      } hover:-translate-y-1`}
    >
      <Link
        to={`/property/${property.id}`}
        className="block relative aspect-[4/3] bg-[#F4F7FB] overflow-hidden"
        data-testid={`property-image-link-${property.id}`}
      >
        {cover ? (
          <img
            src={cover}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#1F2A44] text-sm font-semibold">
            No image
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {property.is_assured_return && (
            <span
              data-testid={`property-badge-assured-${property.id}`}
              className="rc-badge-assured inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] uppercase"
            >
              <BadgeCheck size={12} strokeWidth={3} />
              {property.assured_return_pct
                ? `${property.assured_return_pct}% ${t.badges.assured}`
                : t.badges.assured}
            </span>
          )}
          {property.is_exclusive && (
            <span
              data-testid={`property-badge-exclusive-${property.id}`}
              className="rc-badge-exclusive inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] uppercase"
            >
              <Sparkles size={12} strokeWidth={3} />
              {t.badges.exclusive}
            </span>
          )}
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-[#0B2545] text-[#E9D58F] text-[11px] font-bold uppercase tracking-[0.14em] border border-[#C9A84C]/40">
          {property.listing_type === "rent" ? t.search.rent : t.search.buy}
        </div>
      </Link>

      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-serif text-xl leading-snug text-[#0B2545] line-clamp-2 font-bold">
          {title}
        </h3>
        <RentalBadge property={property} />
        <div className="mt-2 flex items-center text-[#1F2A44] text-sm gap-1.5 font-semibold">
          <MapPin size={14} className="text-[#C9A84C]" strokeWidth={2.5} />
          <span>
            {property.locality ? `${property.locality}, ` : ""}
            {property.city}
          </span>
        </div>

        <div className="mt-4 flex items-center gap-5 text-sm text-[#0B2545] font-semibold">
          {property.bhk ? (
            <span className="inline-flex items-center gap-1.5">
              <BedDouble size={14} className="text-[#C9A84C]" strokeWidth={2.5} />
              {property.bhk} {t.card.bhk}
            </span>
          ) : null}
          {property.sqft ? (
            <span className="inline-flex items-center gap-1.5">
              <Ruler size={14} className="text-[#C9A84C]" strokeWidth={2.5} />
              {property.sqft.toLocaleString()} {t.card.sqft}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1.5 uppercase text-[11px] tracking-[0.18em] text-[#1F2A44] font-bold">
            {property.property_type}
          </span>
        </div>

        <div className="mt-5 flex items-end justify-between gap-3 pt-4 border-t-2 border-[#C9A84C]/30">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#1F2A44] font-bold">
              Investment
            </div>
            <div className="rc-price-gold text-lg md:text-xl font-black mt-0.5 tracking-tight">
              Price on Request
            </div>
          </div>
          <button
            onClick={() => onEnquire?.(property)}
            data-testid={`property-enquire-btn-${property.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#0B2545] text-[#C9A84C] text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[#133763] hover:text-[#E9D58F] transition-all border border-[#C9A84C]/50 shadow-md hover:shadow-lg"
          >
            {t.card.enquire}
            <ArrowRight size={13} strokeWidth={3} />
          </button>
        </div>
      </div>
    </article>
  );
}
